import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { api } from '../api/client';
import { useAuthStore } from '../store/authStore';
import { useNavigate } from 'react-router-dom';

/* ═══════════════════════════════════════════════════════════════
   TYPES
   ═══════════════════════════════════════════════════════════════ */
interface Stats {
  users: { total: number; today: number; last7d: number; prev7d: number; last30d: number; active7d: number; active30d: number; onboarded: number; onboardingRate: number };
  pipelines: { total: number; completed: number; active: number; killed: number; completionRate: number; thisWeek: number; lastWeek: number };
  startups: number;
  credits: { totalPurchased: number; totalSpent: number; avgPerUser: number; totalInCirculation: number };
  revenue: { totalPackRevenue: number; mrr: number; totalRevenue: number; arpu: number };
  proSubscribers: number;
  feedbackCount: number;
  betaUserCount: number;
  scenarioCount: number;
  reviewCount: number;
}

interface AdminUser {
  _id: string; name: string; email: string; createdAt: string; lastLogin: string;
  credits: number; totalPurchased: number; totalSpent: number;
  subscription?: { plan?: string; status?: string };
  pipelineCount: number; startupCount: number; onboardingComplete?: boolean; creditHistoryCount?: number;
}

interface GrowthData {
  weeks: Array<{ week: string; users: number; pipelines: number; revenue: number; usersWoW: number; pipelinesWoW: number; revenueWoW: number }>;
  highlights: { latestUsersWoW: number; latestPipelinesWoW: number; latestRevenueWoW: number };
  velocity: { avgUsersPerWeek: number; avgPipelinesPerWeek: number; avgRevenuePerWeek: number };
  funnel: { signups: number; startedPipeline: number; purchased: number; proSub: number };
}

interface CohortRow { month: string; total: number; retention: Record<number, number | null> }

interface UnitEconData {
  arpu: number; arppu: number; ltv: number; churnRate: number;
  totalRevenue: number; totalPackRevenue: number; totalSubRevenue: number;
  funnel: { signups: number; startedPipeline: number; purchased: number; proSub: number };
  monthlyTrend: Array<{ month: string; pack: number; sub: number; total: number }>;
  topCustomers: Array<{ _id: string; name: string; email: string; totalSpent: number }>;
}

interface EngagementData {
  onboardingRate: number; active7d: number; active30d: number; totalPipelines: number;
  stepFunnel: Array<{ label: string; count: number; pct: number }>;
  medianCompletionDays: number;
  deepAnalysis: { users: number; rate: number };
  featureUsage: Array<{ _id: string; count: number; totalCredits: number }>;
  intelAdoption: Record<string, number>;
}

interface RevenueData {
  dailySignups: Array<{ _id: string; count: number }>;
  creditPurchases: Array<{ _id: string; credits: number; count: number }>;
  packPopularity: Array<{ _id: string; count: number; totalCredits: number }>;
  proSubscribers: number; estimatedMRR: number;
  topCustomers: Array<{ _id: string; name: string; email: string; totalSpent: number; totalPurchased: number; credits: number; subscription?: { status?: string } }>;
  packUpgradeRate: number;
  monthlyRevenue: Array<{ month: string; pack: number; sub: number; total: number }>;
  creditSpending: Array<{ _id: string; totalSpent: number; count: number }>;
}

interface PipelineListData {
  pipelines: Array<{ _id: string; title?: string; status: string; currentStep: number; owner?: { name: string; email: string }; createdAt: string; updatedAt: string; isFirstPipeline: boolean }>;
  total: number; page: number; pages: number;
  funnel: Record<number, number>; status: Record<string, number>;
  completionStats: { medianDays: number; avgDays: number; totalCompleted: number };
}

interface StartupListData {
  startups: Array<{ _id: string; name: string; description?: string; stage: string; owner?: { name: string; email: string }; createdAt: string; signalCount: number; milestoneCount: number; hasDeepAnalysis: boolean; hasMvpBlueprint: boolean; hasIntelligence: boolean; publicShares?: number }>;
  total: number; page: number; pages: number;
  stages: Record<string, number>;
}

interface FeedbackData {
  totalFeedback: number; betaUsers: number;
  nps: { score: number; promoters: number; passives: number; detractors: number; avgNps: number; avgRating: number };
  categories: Record<string, { count: number; avgRating: number }>;
  recentFeedback: Array<{ _id: string; category: string; rating: number; comment: string; npsScore: number; submittedAt: string; betaUser?: { name: string; email: string }; startup?: { name: string } }>;
  featureRequests: Array<{ _id: string; count: number; reasons: string[] }>;
  betaStatuses: Record<string, number>;
}

interface ActivityEvent { type: string; timestamp: string; user: string; email: string; detail: string }

interface AnalyticsData {
  summary: { totalViews: number; uniqueVisitors: number; uniqueUsers: number; avgDuration: number; bounceRate: number };
  viewsByDay?: Array<{ date: string; views: number; visitors: number }>;
  viewsByHour?: Array<{ date: string; views: number; visitors: number }>;
  topPages: Array<{ path: string; views: number; uniqueVisitors: number }>;
  devices: { desktop: number; tablet: number; mobile: number };
  referrers: Array<{ source: string; count: number }>;
  liveUsers: number;
}

type Tab = 'overview' | 'analytics' | 'users' | 'startups' | 'pipelines' | 'revenue' | 'growth' | 'retention' | 'unit-economics' | 'engagement' | 'feedback' | 'activity' | 'settings';

const PACK_PRICES: Record<string, number> = { STARTER: 9.90, GROWTH: 19.90, PRO: 29.90, SPARK: 7, BUILD: 19, LAUNCH: 49, SCALE: 129 };
const STEP_LABELS = ['Profile', 'Idea', 'Reality Check', 'Positioning', 'Validation', 'Complete'];

/* ═══════════════════════════════════════════════════════════════
   SHARED COMPONENTS
   ═══════════════════════════════════════════════════════════════ */
function Kpi({ value, label, sub, trend, size = 'md' }: { value: string | number; label: string; sub?: string; trend?: number | null; size?: 'sm' | 'md' | 'lg' }) {
  const sz = size === 'lg' ? 'text-3xl' : size === 'sm' ? 'text-lg' : 'text-2xl';
  return (
    <div className="bg-gray-900 rounded-xl p-4 border border-gray-800 hover:border-gray-700 transition">
      <div className="flex items-start justify-between gap-2">
        <div className={`${sz} font-bold text-white`}>{typeof value === 'number' ? value.toLocaleString() : value}</div>
        {trend != null && trend !== 0 && (
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${trend > 0 ? 'text-emerald-400 bg-emerald-900/30' : 'text-red-400 bg-red-900/30'}`}>
            {trend > 0 ? '\u25B2' : '\u25BC'}{Math.abs(trend)}%
          </span>
        )}
      </div>
      <div className="text-sm text-gray-400 mt-1">{label}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Bar({ data, color = 'bg-brand-600', label, showLabels }: { data: { value: number; label?: string }[]; color?: string; label: string; showLabels?: boolean }) {
  const allZero = data.every(d => d.value === 0);
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" style={{ minHeight: 200 }}>
      <div className="text-xs text-gray-400 mb-2">{label}</div>
      {allZero || data.length === 0 ? (
        <div className="flex items-center justify-center h-40 text-gray-600 text-xs">No data yet</div>
      ) : (
        <div className="flex items-end gap-0.5" style={{ height: 160, paddingTop: 8 }}>
          {data.map((d, i) => (
            <div key={i} className="flex-1 flex flex-col items-center group relative" style={{ height: '100%', justifyContent: 'flex-end' }}>
              <div className={`w-full ${color} rounded-t transition-all hover:opacity-80`} style={{ height: `${Math.max((d.value / max) * 100, d.value > 0 ? 4 : 0)}%`, minHeight: d.value > 0 ? 4 : 0 }} />
              {showLabels && d.label && <div className="text-[8px] text-gray-600 mt-0.5 truncate w-full text-center">{d.label}</div>}
              <div className="hidden group-hover:block absolute -top-8 bg-gray-800 text-[10px] text-white px-2 py-1 rounded whitespace-nowrap z-10">{d.label}: {d.value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Horiz({ label, count, pct, color }: { label: string; count: number; pct: number; color?: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-32 text-xs text-gray-400 text-right shrink-0 truncate">{label}</div>
      <div className="flex-1 bg-gray-800 rounded-full h-6 overflow-hidden">
        <div className={`h-full ${color || 'bg-brand-600'} rounded-full flex items-center px-2 transition-all`} style={{ width: `${Math.max(pct, 3)}%` }}>
          <span className="text-[10px] text-white font-medium">{count}</span>
        </div>
      </div>
      <div className="w-10 text-xs text-gray-500 text-right">{pct}%</div>
    </div>
  );
}

function Badge({ text, color }: { text: string; color: string }) {
  return <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${color}`}>{text}</span>;
}

function Section({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="bg-gray-900 rounded-xl p-5 border border-gray-800">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-400">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function Loading() { return <div className="text-gray-500 py-8 text-center">Loading...</div>; }

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */
export default function AdminPage() {
  const { logout } = useAuthStore();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('overview');
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [userTotal, setUserTotal] = useState(0);
  const [userPage, setUserPage] = useState(1);
  const [userSearch, setUserSearch] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [userSort, setUserSort] = useState('createdAt');
  const [userOrder, setUserOrder] = useState<'asc' | 'desc'>('desc');
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [userDetail, setUserDetail] = useState<Record<string, unknown> | null>(null);
  const [revenueData, setRevenueData] = useState<RevenueData | null>(null);
  const [growthData, setGrowthData] = useState<GrowthData | null>(null);
  const [cohortData, setCohortData] = useState<CohortRow[] | null>(null);
  const [unitEconData, setUnitEconData] = useState<UnitEconData | null>(null);
  const [engagementData, setEngagementData] = useState<EngagementData | null>(null);
  const [pipelineData, setPipelineData] = useState<PipelineListData | null>(null);
  const [pipelinePage, setPipelinePage] = useState(1);
  const [pipelineFilter, setPipelineFilter] = useState('');
  const [startupData, setStartupData] = useState<StartupListData | null>(null);
  const [startupPage, setStartupPage] = useState(1);
  const [startupFilter, setStartupFilter] = useState('');
  const [feedbackData, setFeedbackData] = useState<FeedbackData | null>(null);
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState<'24h' | '7d' | '30d' | '90d'>('7d');
  const [actionMsg, setActionMsg] = useState('');
  const [negativeCredits, setNegativeCredits] = useState(0);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [refreshAge, setRefreshAge] = useState(0);
  const [slideOver, setSlideOver] = useState<{ type: 'user' | 'startup'; id: string } | null>(null);
  const [slideData, setSlideData] = useState<Record<string, unknown> | null>(null);
  const [modal, setModal] = useState<{ type: 'grant' | 'pro' | 'unpro' | 'delete'; userId: string; name: string; email: string } | null>(null);
  const [modalCredits, setModalCredits] = useState(10);
  const [modalNote, setModalNote] = useState('');
  const [modalConfirmEmail, setModalConfirmEmail] = useState('');
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => { document.title = 'Admin — StartupScore'; }, []);

  useEffect(() => {
    api.get('/admin/stats').then(({ data }) => { setStats(data.data); setLoading(false); })
      .catch((err) => { if (err.response?.status === 403) setDenied(true); setLoading(false); });
  }, []);

  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => { searchTimer.current = setTimeout(() => setDebouncedSearch(userSearch), 300); return () => clearTimeout(searchTimer.current); }, [userSearch]);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/users', { params: { search: debouncedSearch, filter: userFilter, sort: userSort, order: userOrder, page: userPage, limit: 50 } });
      setUsers(data.data.users); setUserTotal(data.data.total);
      // Count negative credits
      const negCount = (data.data.users as AdminUser[]).filter(u => u.credits < 0).length;
      if (negCount > 0) setNegativeCredits(negCount);
    } catch {}
  }, [debouncedSearch, userFilter, userSort, userOrder, userPage]);

  useEffect(() => { if ((tab === 'users' || tab === 'overview') && !denied) loadUsers(); }, [tab, loadUsers, denied]);
  useEffect(() => { if (tab === 'revenue' && !revenueData && !denied) api.get('/admin/revenue').then(({ data }) => setRevenueData(data.data)).catch(() => {}); }, [tab, revenueData, denied]);
  useEffect(() => { if (tab === 'growth' && !growthData && !denied) api.get('/admin/growth').then(({ data }) => setGrowthData(data.data)).catch(() => {}); }, [tab, growthData, denied]);
  useEffect(() => { if (tab === 'retention' && !cohortData && !denied) api.get('/admin/cohorts').then(({ data }) => setCohortData(data.data.cohorts)).catch(() => {}); }, [tab, cohortData, denied]);
  useEffect(() => { if (tab === 'unit-economics' && !unitEconData && !denied) api.get('/admin/unit-economics').then(({ data }) => setUnitEconData(data.data)).catch(() => {}); }, [tab, unitEconData, denied]);
  useEffect(() => { if (tab === 'engagement' && !engagementData && !denied) api.get('/admin/engagement').then(({ data }) => setEngagementData(data.data)).catch(() => {}); }, [tab, engagementData, denied]);
  useEffect(() => { if (tab === 'feedback' && !feedbackData && !denied) api.get('/admin/feedback').then(({ data }) => setFeedbackData(data.data)).catch(() => {}); }, [tab, feedbackData, denied]);
  useEffect(() => { if (tab === 'activity' && activity.length === 0 && !denied) api.get('/admin/activity').then(({ data }) => setActivity(data.data.events)).catch(() => {}); }, [tab, activity.length, denied]);
  useEffect(() => { if (tab === 'analytics' && !denied) { setAnalyticsData(null); api.get('/admin/analytics', { params: { period: analyticsPeriod } }).then(({ data }) => setAnalyticsData(data.data)).catch(() => {}); } }, [tab, analyticsPeriod, denied]);

  useEffect(() => {
    if (tab === 'pipelines' && !denied) {
      api.get('/admin/pipelines', { params: { page: pipelinePage, limit: 30, status: pipelineFilter || undefined } })
        .then(({ data }) => setPipelineData(data.data)).catch(() => {});
    }
  }, [tab, pipelinePage, pipelineFilter, denied]);

  useEffect(() => {
    if (tab === 'startups' && !denied) {
      api.get('/admin/startups', { params: { page: startupPage, limit: 30, stage: startupFilter || undefined } })
        .then(({ data }) => setStartupData(data.data)).catch(() => {});
    }
  }, [tab, startupPage, startupFilter, denied]);

  // Also load revenue on overview
  useEffect(() => { if (tab === 'overview' && !revenueData && !denied) api.get('/admin/revenue').then(({ data }) => setRevenueData(data.data)).catch(() => {}); }, [tab, revenueData, denied]);

  // Auto-refresh timer
  useEffect(() => {
    const ageTimer = setInterval(() => setRefreshAge(Math.floor((Date.now() - lastRefresh) / 1000)), 1000);
    return () => clearInterval(ageTimer);
  }, [lastRefresh]);

  useEffect(() => {
    const autoRefresh = setInterval(() => {
      setLastRefresh(Date.now());
      // Reload current tab data
      api.get('/admin/stats').then(({ data }) => setStats(data.data)).catch(() => {});
      if (tab === 'users') loadUsers();
      if (tab === 'revenue' || tab === 'overview') { setRevenueData(null); api.get('/admin/revenue').then(({ data }) => setRevenueData(data.data)).catch(() => {}); }
    }, 60000);
    return () => clearInterval(autoRefresh);
  }, [tab, loadUsers]); // eslint-disable-line react-hooks/exhaustive-deps

  const manualRefresh = () => {
    setLastRefresh(Date.now());
    api.get('/admin/stats').then(({ data }) => setStats(data.data)).catch(() => {});
    // Reset cached tab data to force reload
    setRevenueData(null); setGrowthData(null); setCohortData(null);
    setUnitEconData(null); setEngagementData(null); setFeedbackData(null);
    setAnalyticsData(null); setActivity([]);
    if (tab === 'users') loadUsers();
  };

  const loadUserDetail = async (id: string) => {
    if (expandedUser === id) { setExpandedUser(null); return; }
    setExpandedUser(id);
    try { const { data } = await api.get(`/admin/users/${id}`); setUserDetail(data.data); } catch { setUserDetail(null); }
  };

  const openSlideOver = async (type: 'user' | 'startup', id: string) => {
    setSlideOver({ type, id }); setSlideData(null);
    try {
      const { data } = await api.get(`/admin/${type === 'user' ? 'users' : 'startups'}/${id}`);
      setSlideData(data.data);
    } catch { setSlideData(null); }
  };

  const toggleSort = (field: string) => {
    if (userSort === field) setUserOrder(userOrder === 'asc' ? 'desc' : 'asc');
    else { setUserSort(field); setUserOrder('desc'); }
    setUserPage(1);
  };

  const openGrantModal = (userId: string, name: string, email: string) => {
    setModalCredits(10); setModalNote(''); setModal({ type: 'grant', userId, name, email });
  };
  const openProModal = (userId: string, name: string, email: string, isPro: boolean) => {
    setModal({ type: isPro ? 'unpro' : 'pro', userId, name, email });
  };
  const openDeleteModal = (userId: string, name: string, email: string) => {
    setModalConfirmEmail(''); setModal({ type: 'delete', userId, name, email });
  };

  const executeModalAction = async () => {
    if (!modal) return;
    setModalLoading(true);
    try {
      if (modal.type === 'grant') {
        await api.post(`/admin/users/${modal.userId}/grant-credits`, { amount: modalCredits, note: modalNote || 'Admin grant' });
        setActionMsg(`✓ ${modalCredits} credits added to ${modal.name}`);
      } else if (modal.type === 'pro') {
        await api.post(`/admin/users/${modal.userId}/toggle-pro`);
        setActionMsg(`✓ ${modal.name} upgraded to Pro`);
      } else if (modal.type === 'unpro') {
        await api.post(`/admin/users/${modal.userId}/toggle-pro`);
        setActionMsg(`✓ Pro removed from ${modal.name}`);
      } else if (modal.type === 'delete') {
        await api.delete(`/admin/users/${modal.userId}`);
        setActionMsg(`✓ Deleted user ${modal.email}`);
      }
      loadUsers();
      setTimeout(() => setActionMsg(''), 4000);
    } catch { setActionMsg('Action failed — please try again'); setTimeout(() => setActionMsg(''), 3000); }
    setModalLoading(false);
    setModal(null);
  };

  if (loading) return <div className="min-h-screen bg-gray-950 flex items-center justify-center"><div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (denied) return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <div className="text-center">
        <div className="text-5xl mb-4">&#128274;</div>
        <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
        <p className="text-gray-400 mb-6">You do not have admin privileges.</p>
        <button onClick={() => navigate('/pipeline')} className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition">Back to App</button>
      </div>
    </div>
  );

  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'analytics', label: 'Analytics' },
    { key: 'users', label: 'Users' },
    { key: 'startups', label: 'Startups' },
    { key: 'pipelines', label: 'Pipelines' },
    { key: 'revenue', label: 'Revenue' },
    { key: 'growth', label: 'Growth' },
    { key: 'retention', label: 'Retention' },
    { key: 'unit-economics', label: 'Unit Econ' },
    { key: 'engagement', label: 'Engagement' },
    { key: 'feedback', label: 'Feedback' },
    { key: 'activity', label: 'Activity' },
    { key: 'settings', label: '⚙️ Settings' },
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      <header className="bg-gray-900 border-b border-gray-800 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xl font-bold text-white" style={{ letterSpacing: '-0.3px' }}>
            Startup<span style={{ color: '#f97316' }}>Score</span>
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 9, fontWeight: 800, letterSpacing: 1.2, color: '#22c55e', marginLeft: -4 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', display: 'inline-block', boxShadow: '0 0 6px rgba(34,197,94,0.6)' }} />
            LIVE
          </span>
          <span className="text-sm text-gray-500 border-l border-gray-700 pl-3">Admin</span>
        </div>
        <div className="flex items-center gap-4">
          {actionMsg && <span className="text-xs text-emerald-400 bg-emerald-900/30 px-3 py-1 rounded-full animate-pulse">{actionMsg}</span>}
          <span className="text-[10px] text-gray-600">{refreshAge}s ago</span>
          <button onClick={manualRefresh} className="text-[10px] text-gray-400 hover:text-white transition px-2 py-1 bg-gray-800 rounded" title="Refresh data">↻ Refresh</button>
          <button onClick={() => { logout(); navigate('/auth'); }} className="text-sm text-gray-400 hover:text-white transition">Logout</button>
        </div>
      </header>

      <nav className="bg-gray-900 border-b border-gray-800 px-6 flex gap-0.5 overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-2.5 text-xs font-medium border-b-2 transition whitespace-nowrap ${tab === t.key ? 'border-brand-500 text-brand-400' : 'border-transparent text-gray-500 hover:text-gray-300'}`}>
            {t.label}
          </button>
        ))}
      </nav>

      <main className="max-w-[1400px] mx-auto px-4 py-6">
        {tab === 'overview' && stats && <OverviewTab stats={stats} revenue={revenueData} negativeCredits={negativeCredits} />}
        {tab === 'analytics' && <AnalyticsTab data={analyticsData} period={analyticsPeriod} setPeriod={setAnalyticsPeriod} />}
        {tab === 'users' && <UsersTab users={users} total={userTotal} page={userPage} search={userSearch} filter={userFilter}
          setSearch={(s) => { setUserSearch(s); setUserPage(1); }} setFilter={(f) => { setUserFilter(f); setUserPage(1); }}
          setPage={setUserPage} sort={userSort} order={userOrder} toggleSort={toggleSort}
          expandedUser={expandedUser} userDetail={userDetail} onExpand={loadUserDetail}
          onGrant={openGrantModal} onTogglePro={openProModal} onDelete={openDeleteModal}
          onClickUser={(id) => openSlideOver('user', id)} />}

        {/* ── Admin Action Modals ── */}
        {modal && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={() => !modalLoading && setModal(null)} />
            <div style={{ position: 'relative', background: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: 12, padding: 24, width: 420, maxWidth: '90vw' }}>
              {modal.type === 'grant' && (
                <>
                  <h3 className="text-white font-semibold text-sm mb-4">Grant Credits to {modal.name}</h3>
                  <label className="text-xs text-gray-400 block mb-1">Credits to add</label>
                  <input type="number" min={1} max={500} value={modalCredits} onChange={e => setModalCredits(Math.max(1, Math.min(500, parseInt(e.target.value) || 1)))}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white mb-3 focus:outline-none focus:border-brand-500" />
                  <label className="text-xs text-gray-400 block mb-1">Reason (internal note)</label>
                  <input type="text" value={modalNote} onChange={e => setModalNote(e.target.value)} placeholder="e.g. beta reward, support"
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white mb-4 focus:outline-none focus:border-brand-500" />
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setModal(null)} disabled={modalLoading} className="px-4 py-2 text-xs bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition">Cancel</button>
                    <button onClick={executeModalAction} disabled={modalLoading} className="px-4 py-2 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition font-medium">
                      {modalLoading ? 'Granting...' : 'Grant Credits →'}
                    </button>
                  </div>
                </>
              )}
              {modal.type === 'pro' && (
                <>
                  <h3 className="text-white font-semibold text-sm mb-2">Upgrade {modal.name} to Pro?</h3>
                  <p className="text-xs text-gray-400 mb-4">This will grant Pro access immediately. No payment will be charged.</p>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setModal(null)} disabled={modalLoading} className="px-4 py-2 text-xs bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition">Cancel</button>
                    <button onClick={executeModalAction} disabled={modalLoading} className="px-4 py-2 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-medium">
                      {modalLoading ? 'Upgrading...' : 'Confirm Upgrade'}
                    </button>
                  </div>
                </>
              )}
              {modal.type === 'unpro' && (
                <>
                  <h3 className="text-white font-semibold text-sm mb-2">Remove Pro from {modal.name}?</h3>
                  <p className="text-xs text-gray-400 mb-4">This will immediately revoke Pro access.</p>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setModal(null)} disabled={modalLoading} className="px-4 py-2 text-xs bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition">Cancel</button>
                    <button onClick={executeModalAction} disabled={modalLoading} className="px-4 py-2 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium">
                      {modalLoading ? 'Removing...' : 'Confirm Remove'}
                    </button>
                  </div>
                </>
              )}
              {modal.type === 'delete' && (
                <>
                  <h3 className="text-red-400 font-semibold text-sm mb-2">Delete {modal.name}?</h3>
                  <p className="text-xs text-gray-400 mb-3">This will permanently delete the user and all their data. This cannot be undone.</p>
                  <label className="text-xs text-gray-400 block mb-1">Type the user's email to confirm: <span className="text-white font-mono">{modal.email}</span></label>
                  <input type="text" value={modalConfirmEmail} onChange={e => setModalConfirmEmail(e.target.value)} placeholder={modal.email}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white mb-4 focus:outline-none focus:border-red-500 font-mono" />
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setModal(null)} disabled={modalLoading} className="px-4 py-2 text-xs bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition">Cancel</button>
                    <button onClick={executeModalAction} disabled={modalLoading || modalConfirmEmail !== modal.email}
                      className="px-4 py-2 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium disabled:opacity-40 disabled:cursor-not-allowed">
                      {modalLoading ? 'Deleting...' : 'Delete Permanently'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {tab === 'startups' && <StartupsTab data={startupData} page={startupPage} setPage={setStartupPage} filter={startupFilter} setFilter={setStartupFilter} onClickStartup={(id) => openSlideOver('startup', id)} />}
        {tab === 'pipelines' && <PipelinesTab data={pipelineData} page={pipelinePage} setPage={setPipelinePage} filter={pipelineFilter} setFilter={setPipelineFilter} onClickPipeline={(ownerId) => { if (ownerId) openSlideOver('user', ownerId); }} />}
        {tab === 'revenue' && <RevenueTab data={revenueData} />}
        {tab === 'growth' && <GrowthTab data={growthData} />}
        {tab === 'retention' && <RetentionTab data={cohortData} />}
        {tab === 'unit-economics' && <UnitEconomicsTab data={unitEconData} />}
        {tab === 'engagement' && <EngagementTab data={engagementData} />}
        {tab === 'feedback' && <FeedbackTab data={feedbackData} />}
        {tab === 'activity' && <ActivityTab events={activity} />}
        {tab === 'settings' && <SettingsTab />}
      </main>

      {/* ── Slide-over Panel ── */}
      {slideOver && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 90, display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={() => setSlideOver(null)} />
          <div style={{ position: 'relative', width: 480, maxWidth: '90vw', background: '#111122', borderLeft: '1px solid #2a2a3e', overflowY: 'auto', padding: 24 }}>
            <button onClick={() => setSlideOver(null)} style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', color: '#888', fontSize: 18, cursor: 'pointer' }}>✕</button>
            {!slideData ? <Loading /> : (
              slideOver.type === 'user'
                ? <UserSlideOver data={slideData} onClose={() => setSlideOver(null)} onGrant={openGrantModal} onTogglePro={openProModal} onDelete={openDeleteModal} />
                : <StartupSlideOver data={slideData} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TAB 1: OVERVIEW — Command Center
   ═══════════════════════════════════════════════════════════════ */
function OverviewTab({ stats: s, revenue, negativeCredits }: { stats: Stats; revenue: RevenueData | null; negativeCredits?: number }) {
  const ut = s.users.prev7d > 0 ? Math.round(((s.users.last7d - s.users.prev7d) / s.users.prev7d) * 100) : null;
  const pt = s.pipelines.lastWeek > 0 ? Math.round(((s.pipelines.thisWeek - s.pipelines.lastWeek) / s.pipelines.lastWeek) * 100) : null;

  return (
    <div className="space-y-5">
      {/* Row 1: Hero KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <Kpi value={s.users.total} label="Total Users" sub={`+${s.users.last7d} this week`} trend={ut} />
        <Kpi value={`$${s.revenue.mrr}`} label="MRR" sub={`${s.proSubscribers} Pro subs`} />
        <Kpi value={`$${s.revenue.totalRevenue.toLocaleString()}`} label="Total Revenue" sub={`Pack $${s.revenue.totalPackRevenue} + Sub $${s.revenue.mrr}`} />
        <Kpi value={s.pipelines.total} label="Pipelines" sub={`${s.pipelines.completed} completed`} trend={pt} />
        <Kpi value={s.startups} label="Startups" sub={`${s.scenarioCount} scenarios`} />
        <Kpi value={s.users.active7d} label="Active 7d" sub={`${s.users.total > 0 ? Math.round((s.users.active7d / s.users.total) * 100) : 0}% of total`} />
        <Kpi value={`${s.users.onboardingRate}%`} label="Onboarding" sub={`${s.users.onboarded} completed`} />
        <Kpi value={`$${s.revenue.arpu}`} label="ARPU" sub="Rev / total users" />
      </div>

      {/* Negative credits alert */}
      {negativeCredits != null && negativeCredits > 0 && (
        <div className="bg-red-900/20 border border-red-800/50 rounded-xl px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-red-400 text-sm">⚠️</span>
            <span className="text-red-300 text-xs font-medium">{negativeCredits} user{negativeCredits > 1 ? 's' : ''} with negative credits</span>
          </div>
          <span className="text-red-400 text-[10px]">Check Users tab for affected accounts</span>
        </div>
      )}

      {/* Row 2: Platform Health */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Kpi value={s.credits.totalInCirculation} label="Credits in Circulation" sub={`${s.credits.avgPerUser} avg/user`} size="sm" />
        <Kpi value={s.credits.totalPurchased} label="Credits Purchased" sub={`${s.credits.totalSpent} spent`} size="sm" />
        <Kpi value={s.feedbackCount} label="Feedback Received" sub={`${s.betaUserCount} beta users`} size="sm" />
        <Kpi value={s.reviewCount} label="Pipeline Reviews" size="sm" />
        <Kpi value={`${s.pipelines.completionRate}%`} label="Pipeline Completion" sub={`${s.pipelines.active} active, ${s.pipelines.killed} killed`} size="sm" />
        <Kpi value={s.users.today} label="Signups Today" sub={`${s.users.last30d} this month`} size="sm" />
      </div>

      {/* Row 3: Charts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Bar data={revenue?.dailySignups?.slice(-14).map(d => ({ value: d.count, label: d._id.slice(5) })) || []} label="Daily Signups (14d)" color="bg-purple-500" showLabels />
        <Bar data={revenue?.monthlyRevenue?.map(d => ({ value: d.total, label: d.month.slice(5) })) || []} label="Monthly Revenue" color="bg-orange-500" showLabels />
        <Bar data={[
          { value: s.pipelines.lastWeek, label: 'Last wk' },
          { value: s.pipelines.thisWeek, label: 'This wk' },
        ]} label="Pipelines per Week" color="bg-blue-500" showLabels />
      </div>

      {/* Row 4: Pack breakdown */}
      {revenue && revenue.packPopularity.length > 0 && (
        <Section title="Credit Pack Sales">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {revenue.packPopularity.map(p => (
              <div key={p._id} className="bg-gray-800/40 rounded-lg p-3">
                <div className="text-sm font-bold text-white">{p._id}</div>
                <div className="text-xs text-gray-400 mt-1">{p.count} sales &middot; {p.totalCredits} credits</div>
                <div className="text-sm text-green-400 font-medium mt-1">${p.count * (PACK_PRICES[p._id] || 0)}</div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TAB 2: USERS — Full Management
   ═══════════════════════════════════════════════════════════════ */
function UsersTab({ users, total, page, search, filter, setSearch, setFilter, setPage, sort, order, toggleSort, expandedUser, userDetail, onExpand, onGrant, onTogglePro, onDelete, onClickUser }: {
  users: AdminUser[]; total: number; page: number; search: string; filter: string;
  setSearch: (s: string) => void; setFilter: (f: string) => void; setPage: (p: number) => void;
  sort: string; order: 'asc' | 'desc'; toggleSort: (f: string) => void;
  expandedUser: string | null; userDetail: Record<string, unknown> | null; onExpand: (id: string) => void;
  onGrant: (id: string, name: string, email: string) => void; onTogglePro: (id: string, name: string, email: string, isPro: boolean) => void; onDelete: (id: string, name: string, email: string) => void;
  onClickUser?: (id: string) => void;
}) {
  const pages = Math.ceil(total / 50);
  const fourteenDaysAgo = Date.now() - 14 * 86400000;
  const sortIcon = (f: string) => sort !== f ? '' : order === 'asc' ? ' \u2191' : ' \u2193';
  const dot = (u: AdminUser) => u.subscription?.status === 'active' ? 'bg-green-400' : u.lastLogin && new Date(u.lastLogin).getTime() > fourteenDaysAgo ? 'bg-orange-400' : 'bg-gray-600';

  const filters = [
    { key: '', label: 'All' }, { key: 'pro', label: 'Pro' }, { key: 'paying', label: 'Paying' },
    { key: 'new7d', label: 'New 7d' }, { key: 'inactive', label: 'Inactive 30d' },
  ];
  const cols = [
    { key: 'name', label: 'Name' }, { key: 'email', label: 'Email' }, { key: 'createdAt', label: 'Joined' },
    { key: 'lastLogin', label: 'Last Active' }, { key: 'credits', label: 'Credits' }, { key: 'totalSpent', label: 'Spent' }, { key: 'pipelineCount', label: 'Pipes' },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <input type="text" placeholder="Search name or email..." value={search} onChange={e => setSearch(e.target.value)}
          className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-brand-500 w-72" />
        <div className="flex gap-1">
          {filters.map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-xs rounded-lg transition ${filter === f.key ? 'bg-brand-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>{f.label}</button>
          ))}
        </div>
        <span className="text-xs text-gray-500 ml-auto">{total} users</span>
        <button onClick={() => {
          const csv = ['Name,Email,Joined,Last Active,Credits,Spent,Pipelines,Pro',
            ...users.map(u => `"${u.name}","${u.email}",${new Date(u.createdAt).toLocaleDateString()},${u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : 'Never'},${u.credits},${u.totalSpent},${u.pipelineCount},${u.subscription?.status === 'active' ? 'Yes' : 'No'}`)
          ].join('\n');
          const blob = new Blob([csv], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a'); a.href = url; a.download = `startupscore-users-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
          URL.revokeObjectURL(url);
        }} className="px-3 py-1.5 text-xs bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition">⬇ Export CSV</button>
      </div>

      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="w-6 px-3 py-2" />
              {cols.map(c => (
                <th key={c.key} onClick={() => toggleSort(c.key)} className="px-3 py-2 text-left text-gray-400 font-medium cursor-pointer hover:text-white transition whitespace-nowrap">{c.label}{sortIcon(c.key)}</th>
              ))}
              <th className="px-3 py-2 text-gray-400 font-medium">Status</th>
              <th className="px-3 py-2 text-gray-400 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <Fragment key={u._id}>
                <tr onClick={() => onClickUser ? onClickUser(u._id) : onExpand(u._id)} className="border-b border-gray-800/50 hover:bg-gray-800/30 cursor-pointer transition">
                  <td className="px-3 py-2"><div className={`w-2 h-2 rounded-full ${dot(u)}`} /></td>
                  <td className="px-3 py-2 text-white font-medium">{u.name}</td>
                  <td className="px-3 py-2 text-gray-300">{u.email}</td>
                  <td className="px-3 py-2 text-gray-400">{new Date(u.createdAt).toLocaleDateString()}</td>
                  <td className="px-3 py-2 text-gray-400">{u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : 'Never'}</td>
                  <td className={`px-3 py-2 ${u.credits < 0 ? 'text-red-400 font-medium' : 'text-gray-300'}`}>{u.credits}</td>
                  <td className="px-3 py-2 text-gray-300">{u.totalSpent}</td>
                  <td className="px-3 py-2 text-gray-300">{u.pipelineCount}</td>
                  <td className="px-3 py-2">
                    {u.subscription?.status === 'active' && <Badge text="PRO" color="bg-purple-900/50 text-purple-400" />}
                    {u.onboardingComplete && <Badge text="OB" color="bg-emerald-900/50 text-emerald-400 ml-1" />}
                  </td>
                  <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <button onClick={() => onGrant(u._id, u.name, u.email)} className="px-2 py-1 text-[10px] bg-emerald-900/30 text-emerald-400 rounded hover:bg-emerald-900/50 transition" title="Grant credits">+Cr</button>
                      <button onClick={() => onTogglePro(u._id, u.name, u.email, u.subscription?.status === 'active')}
                        className={`px-2 py-1 text-[10px] rounded transition ${u.subscription?.status === 'active' ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50' : 'bg-purple-900/30 text-purple-400 hover:bg-purple-900/50'}`}>
                        {u.subscription?.status === 'active' ? '-Pro' : '+Pro'}
                      </button>
                      <button onClick={() => onDelete(u._id, u.name, u.email)} className="px-2 py-1 text-[10px] bg-red-900/20 text-red-500 rounded hover:bg-red-900/40 transition" title="Delete user">Del</button>
                    </div>
                  </td>
                </tr>
                {expandedUser === u._id && userDetail && (
                  <tr className="bg-gray-800/20"><td colSpan={10} className="px-4 py-3"><UserDetailPanel detail={userDetail} /></td></tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {pages > 1 && <Pagination page={page} pages={pages} setPage={setPage} />}
    </div>
  );
}

function UserDetailPanel({ detail }: { detail: Record<string, unknown> }) {
  const user = detail.user as Record<string, unknown> | undefined;
  const pipelines = (detail.pipelines as Array<Record<string, unknown>>) || [];
  const startups = (detail.startups as Array<Record<string, unknown>>) || [];
  const creditHistory = ((user?.creditHistory as Array<Record<string, unknown>>) || []).slice(-15).reverse();
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
      <div>
        <h4 className="font-medium text-gray-300 mb-2">Credit History (last 15)</h4>
        {creditHistory.length === 0 && <p className="text-gray-500">No history</p>}
        {creditHistory.map((ch, i) => (
          <div key={i} className="flex justify-between py-0.5 border-b border-gray-800/50">
            <span className="text-gray-400 truncate">{ch.action as string} {ch.pack ? `(${ch.pack})` : ''} {ch.note ? `- ${ch.note}` : ''}</span>
            <span className={`font-medium shrink-0 ml-2 ${(ch.amount as number) > 0 ? 'text-green-400' : 'text-red-400'}`}>{(ch.amount as number) > 0 ? '+' : ''}{ch.amount as number}</span>
          </div>
        ))}
      </div>
      <div>
        <h4 className="font-medium text-gray-300 mb-2">Pipelines ({pipelines.length})</h4>
        {pipelines.map(p => (
          <div key={p._id as string} className="py-0.5 border-b border-gray-800/50 flex justify-between">
            <span className="text-gray-300 truncate">{(p.title as string) || 'Untitled'}</span>
            <Badge text={p.status as string} color={p.status === 'completed' ? 'bg-green-900/50 text-green-400' : p.status === 'active' ? 'bg-blue-900/50 text-blue-400' : 'bg-gray-700 text-gray-400'} />
          </div>
        ))}
      </div>
      <div>
        <h4 className="font-medium text-gray-300 mb-2">Startups ({startups.length})</h4>
        {startups.map(s => (
          <div key={s._id as string} className="py-0.5 border-b border-gray-800/50 text-gray-300 flex justify-between">
            <span className="truncate">{s.name as string}</span>
            <Badge text={s.stage as string} color="bg-gray-700 text-gray-400" />
          </div>
        ))}
        {user && <div className="mt-2 text-gray-500">Sub: {(user.subscription as Record<string, string>)?.status || 'none'} | Onboarding: {user.onboardingComplete ? 'Yes' : 'No'}</div>}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TAB 3: STARTUPS
   ═══════════════════════════════════════════════════════════════ */
function StartupsTab({ data, page, setPage, filter, setFilter, onClickStartup }: { data: StartupListData | null; page: number; setPage: (p: number) => void; filter: string; setFilter: (f: string) => void; onClickStartup?: (id: string) => void }) {
  if (!data) return <Loading />;
  const stages = ['', 'idea', 'validation', 'mvp', 'growth'];
  const stageColors: Record<string, string> = { idea: 'bg-blue-900/50 text-blue-400', validation: 'bg-yellow-900/50 text-yellow-400', mvp: 'bg-emerald-900/50 text-emerald-400', growth: 'bg-purple-900/50 text-purple-400' };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1">
          {stages.map(s => (
            <button key={s} onClick={() => { setFilter(s); setPage(1); }}
              className={`px-3 py-1.5 text-xs rounded-lg transition ${filter === s ? 'bg-brand-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
              {s || 'All'} {data.stages[s] !== undefined ? `(${data.stages[s]})` : s === '' ? `(${data.total})` : ''}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-500 ml-auto">{data.total} startups</span>
      </div>

      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="border-b border-gray-800">
            <th className="px-3 py-2 text-left text-gray-400">Name</th>
            <th className="px-3 py-2 text-left text-gray-400">Owner</th>
            <th className="px-3 py-2 text-gray-400">Stage</th>
            <th className="px-3 py-2 text-gray-400">Signals</th>
            <th className="px-3 py-2 text-gray-400">Milestones</th>
            <th className="px-3 py-2 text-gray-400">Features</th>
            <th className="px-3 py-2 text-gray-400">Created</th>
          </tr></thead>
          <tbody>
            {data.startups.map(s => (
              <tr key={s._id} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition cursor-pointer" onClick={() => onClickStartup?.(s._id)}>
                <td className="px-3 py-2 text-white font-medium max-w-[200px] truncate">{s.name}</td>
                <td className="px-3 py-2 text-gray-300">{s.owner?.name || '?'} <span className="text-gray-500">({s.owner?.email})</span></td>
                <td className="px-3 py-2 text-center"><Badge text={s.stage} color={stageColors[s.stage] || 'bg-gray-700 text-gray-400'} /></td>
                <td className="px-3 py-2 text-center text-gray-300">{s.signalCount}</td>
                <td className="px-3 py-2 text-center text-gray-300">{s.milestoneCount}</td>
                <td className="px-3 py-2 text-center">
                  {s.hasDeepAnalysis && <Badge text="Deep" color="bg-orange-900/50 text-orange-400 mr-0.5" />}
                  {s.hasMvpBlueprint && <Badge text="MVP" color="bg-emerald-900/50 text-emerald-400 mr-0.5" />}
                  {s.hasIntelligence && <Badge text="Intel" color="bg-blue-900/50 text-blue-400" />}
                </td>
                <td className="px-3 py-2 text-gray-400">{new Date(s.createdAt).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.pages > 1 && <Pagination page={page} pages={data.pages} setPage={setPage} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TAB 4: PIPELINES
   ═══════════════════════════════════════════════════════════════ */
function PipelinesTab({ data, page, setPage, filter, setFilter, onClickPipeline }: { data: PipelineListData | null; page: number; setPage: (p: number) => void; filter: string; setFilter: (f: string) => void; onClickPipeline?: (ownerId: string) => void }) {
  if (!data) return <Loading />;
  const statuses = ['', 'active', 'completed', 'killed', 'incubator'];
  const statusColors: Record<string, string> = { active: 'bg-blue-900/50 text-blue-400', completed: 'bg-green-900/50 text-green-400', killed: 'bg-red-900/50 text-red-400', incubator: 'bg-yellow-900/50 text-yellow-400' };
  const funnelMax = Math.max(...Object.values(data.funnel), 1);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        {statuses.map(s => (
          <button key={s} onClick={() => { setFilter(s); setPage(1); }}
            className={`px-3 py-1.5 text-xs rounded-lg transition ${filter === s ? 'bg-brand-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
            {s || 'All'} ({s ? (data.status[s] || 0) : data.total})
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Kpi value={data.completionStats.totalCompleted} label="Completed" sub={`Median: ${data.completionStats.medianDays}d | Avg: ${data.completionStats.avgDays}d`} />
        <Kpi value={data.status.active || 0} label="Active Now" sub={`${data.status.killed || 0} killed`} />
        <Kpi value={data.status.incubator || 0} label="In Incubator" />
      </div>

      {/* Step Funnel */}
      <Section title="Step Distribution (All Pipelines)">
        <div className="space-y-1.5">
          {STEP_LABELS.map((label, i) => {
            // Collapse steps ≥5 (Payment Test, MVP Blueprint, etc.) into "Complete"
            const count = i === 5
              ? (data.funnel[5] || 0) + (data.funnel[6] || 0) + (data.funnel[7] || 0)
              : (data.funnel[i] || 0);
            const funnelMaxAdj = Math.max(...STEP_LABELS.map((_, j) => j === 5
              ? (data.funnel[5] || 0) + (data.funnel[6] || 0) + (data.funnel[7] || 0)
              : (data.funnel[j] || 0)), 1);
            return <Horiz key={i} label={`${i}: ${label}`} count={count} pct={Math.round((count / funnelMaxAdj) * 100)} />;
          })}
        </div>
      </Section>

      {/* Pipeline list */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="border-b border-gray-800">
            <th className="px-3 py-2 text-left text-gray-400">Title</th>
            <th className="px-3 py-2 text-left text-gray-400">Owner</th>
            <th className="px-3 py-2 text-gray-400">Status</th>
            <th className="px-3 py-2 text-gray-400">Step</th>
            <th className="px-3 py-2 text-gray-400">First?</th>
            <th className="px-3 py-2 text-gray-400">Created</th>
          </tr></thead>
          <tbody>
            {data.pipelines.map(p => (
              <tr key={p._id} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition cursor-pointer" onClick={() => onClickPipeline?.((p.owner as unknown as { _id: string })?._id || '')}>
                <td className="px-3 py-2 text-white font-medium max-w-[200px] truncate">{p.title || 'Untitled'}</td>
                <td className="px-3 py-2 text-gray-300">{p.owner?.name} <span className="text-gray-500">({p.owner?.email})</span></td>
                <td className="px-3 py-2 text-center"><Badge text={p.status} color={statusColors[p.status] || 'bg-gray-700 text-gray-400'} /></td>
                <td className="px-3 py-2 text-center text-gray-300">{Math.min(p.currentStep, 5)}/5</td>
                <td className="px-3 py-2 text-center">{p.isFirstPipeline ? <span className="text-green-400">Yes</span> : <span className="text-gray-500">No</span>}</td>
                <td className="px-3 py-2 text-gray-400">{new Date(p.createdAt).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.pages > 1 && <Pagination page={page} pages={data.pages} setPage={setPage} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TAB 5: REVENUE
   ═══════════════════════════════════════════════════════════════ */
function RevenueTab({ data }: { data: RevenueData | null }) {
  if (!data) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi value={`$${data.estimatedMRR}`} label="MRR" sub={`${data.proSubscribers} Pro subscribers`} size="lg" />
        <Kpi value={`${data.packUpgradeRate}%`} label="Pack Upgrade Rate" sub="2+ distinct packs" />
        <Kpi value={data.packPopularity.reduce((s, p) => s + p.count, 0)} label="Total Pack Sales" sub={`${data.packPopularity.reduce((s, p) => s + p.count * (PACK_PRICES[p._id] || 0), 0)} revenue`} />
        <Kpi value={data.creditSpending.reduce((s, c) => s + c.totalSpent, 0)} label="Credits Consumed" sub={`Across ${data.creditSpending.reduce((s, c) => s + c.count, 0)} actions`} />
      </div>

      {/* Monthly revenue chart */}
      <Section title="Monthly Revenue Trend">
        {data.monthlyRevenue.length === 0 || data.monthlyRevenue.every(m => m.total === 0) ? (
          <div className="flex items-center justify-center text-gray-600 text-xs" style={{ minHeight: 200 }}>No revenue data yet</div>
        ) : (
          <>
            <div className="flex items-end gap-3" style={{ height: 200 }}>
              {data.monthlyRevenue.map(m => {
                const max = Math.max(...data.monthlyRevenue.map(x => x.total), 1);
                const packH = Math.max((m.pack / max) * 100, m.pack > 0 ? 3 : 0);
                const subH = Math.max((m.sub / max) * 100, m.sub > 0 ? 3 : 0);
                return (
                  <div key={m.month} className="flex-1 flex flex-col items-center group relative" style={{ height: '100%', justifyContent: 'flex-end' }}>
                    <div className="w-full flex flex-col justify-end" style={{ height: '100%' }}>
                      <div className="bg-purple-600 rounded-t" style={{ height: `${subH}%`, minHeight: m.sub > 0 ? 3 : 0 }} />
                      <div className="bg-orange-500" style={{ height: `${packH}%`, minHeight: m.pack > 0 ? 3 : 0 }} />
                    </div>
                    <div className="text-[9px] text-gray-500 mt-1">{m.month.slice(5)}</div>
                    <div className="hidden group-hover:block absolute -top-10 bg-gray-800 text-[10px] text-white px-2 py-1 rounded whitespace-nowrap z-10">
                      {m.month}: Pack ${m.pack} + Sub ${m.sub} = ${m.total}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-4 mt-2 text-[10px] text-gray-500">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-orange-500 rounded" /> Packs</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-purple-600 rounded" /> Subscriptions</span>
            </div>
          </>
        )}
      </Section>

      {/* Pack breakdown + spending */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title="Pack Sales Breakdown">
          {data.packPopularity.length === 0 ? (
            <div className="text-center py-6">
              <div className="text-gray-500 text-xs">No pack sales yet</div>
              <div className="text-gray-600 text-[10px] mt-1">First sale will appear here</div>
            </div>
          ) : (
            <div className="space-y-2">
              {data.packPopularity.map(p => (
                <div key={p._id} className="flex items-center justify-between py-1.5 border-b border-gray-800/50">
                  <div><span className="text-white font-medium">{p._id}</span> <span className="text-gray-500 text-xs">&middot; ${PACK_PRICES[p._id] || 0}/pack</span></div>
                  <div className="text-right"><span className="text-green-400 font-medium">${p.count * (PACK_PRICES[p._id] || 0)}</span> <span className="text-gray-500 text-xs ml-1">({p.count} sales)</span></div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Credit Spending by Feature">
          <div className="space-y-1.5">
            {data.creditSpending.slice(0, 8).map(c => {
              const maxSpent = data.creditSpending[0]?.totalSpent || 1;
              return <Horiz key={c._id} label={c._id} count={c.totalSpent} pct={Math.round((c.totalSpent / maxSpent) * 100)} color="bg-orange-500" />;
            })}
          </div>
        </Section>
      </div>

      {/* Top customers */}
      {data.topCustomers.length > 0 && (
        <Section title={`Top ${data.topCustomers.length} Customers`}>
          <div className="space-y-1.5">
            {data.topCustomers.map((c, i) => (
              <div key={c._id} className="flex items-center justify-between py-1.5 border-b border-gray-800/50 text-xs">
                <div><span className="text-gray-500">#{i + 1}</span> <span className="text-white font-medium ml-1">{c.name}</span> <span className="text-gray-500 ml-1">{c.email}</span></div>
                <div className="flex items-center gap-3">
                  <span className="text-gray-400">{c.credits} credits left</span>
                  <span className="text-green-400 font-medium">{c.totalSpent} spent</span>
                  {c.subscription?.status === 'active' && <Badge text="PRO" color="bg-purple-900/50 text-purple-400" />}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TAB 6: GROWTH
   ═══════════════════════════════════════════════════════════════ */
function GrowthTab({ data }: { data: GrowthData | null }) {
  const [metric, setMetric] = useState<'users' | 'pipelines' | 'revenue'>('users');
  if (!data) return <Loading />;
  const weeks = data.weeks;
  const values = weeks.map(w => w[metric]);
  const wows = weeks.map(w => metric === 'users' ? w.usersWoW : metric === 'pipelines' ? w.pipelinesWoW : w.revenueWoW);
  const max = Math.max(...values, 1);

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {(['users', 'pipelines', 'revenue'] as const).map(m => (
          <button key={m} onClick={() => setMetric(m)} className={`px-4 py-1.5 text-xs rounded-lg transition ${metric === m ? 'bg-brand-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
            {m.charAt(0).toUpperCase() + m.slice(1)}
          </button>
        ))}
      </div>

      <Section title={`Weekly ${metric} (Last 12 Weeks)`}>
        {weeks.length === 0 || values.every(v => v === 0) ? (
          <div className="flex items-center justify-center text-gray-600 text-xs" style={{ minHeight: 200 }}>No data yet</div>
        ) : (
          <div className="flex items-end gap-2" style={{ height: 200 }}>
            {weeks.map((w, i) => {
              const barColor = metric === 'users' ? 'bg-purple-500' : metric === 'pipelines' ? 'bg-blue-500' : 'bg-orange-500';
              return (
                <div key={w.week} className="flex-1 flex flex-col items-center group relative" style={{ height: '100%', justifyContent: 'flex-end' }}>
                  {wows[i] !== 0 && <div className={`text-[9px] mb-0.5 ${wows[i] > 0 ? 'text-emerald-400' : 'text-red-400'}`}>{wows[i] > 0 ? '+' : ''}{wows[i]}%</div>}
                  <div className={`w-full ${barColor} rounded-t hover:opacity-80 transition-all`} style={{ height: `${Math.max((values[i] / max) * 100, values[i] > 0 ? 4 : 0)}%`, minHeight: values[i] > 0 ? 4 : 0 }} />
                  <div className="text-[8px] text-gray-600 mt-0.5">{w.week.slice(-3)}</div>
                  <div className="hidden group-hover:block absolute -top-8 bg-gray-800 text-[10px] text-white px-2 py-1 rounded whitespace-nowrap z-10">{w.week}: {metric === 'revenue' ? `$${values[i]}` : values[i]}</div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Kpi value={data.velocity.avgUsersPerWeek} label="Avg Users/Week" trend={data.highlights.latestUsersWoW} />
        <Kpi value={data.velocity.avgPipelinesPerWeek} label="Avg Pipelines/Week" trend={data.highlights.latestPipelinesWoW} />
        <Kpi value={`$${data.velocity.avgRevenuePerWeek}`} label="Avg Revenue/Week" trend={data.highlights.latestRevenueWoW} />
      </div>

      <Section title="Acquisition Funnel">
        <div className="space-y-2">
          {[
            { label: 'Signed Up', count: data.funnel.signups },
            { label: 'Started Pipeline', count: data.funnel.startedPipeline },
            { label: 'Purchased Credits', count: data.funnel.purchased },
            { label: 'Pro Subscriber', count: data.funnel.proSub },
          ].map(step => {
            const pct = data.funnel.signups > 0 ? Math.round((step.count / data.funnel.signups) * 100) : 0;
            return <Horiz key={step.label} label={step.label} count={step.count} pct={pct} />;
          })}
        </div>
      </Section>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TAB 7: RETENTION
   ═══════════════════════════════════════════════════════════════ */
function RetentionTab({ data }: { data: CohortRow[] | null }) {
  if (!data) return <Loading />;
  const periods = [7, 14, 30, 60];
  const cellColor = (v: number | null) => {
    if (v === null) return 'text-gray-600';
    if (v >= 60) return 'bg-emerald-900/40 text-emerald-300';
    if (v >= 40) return 'bg-teal-900/40 text-teal-300';
    if (v >= 20) return 'bg-yellow-900/40 text-yellow-300';
    return 'bg-red-900/40 text-red-300';
  };

  return (
    <div className="space-y-4">
      <Section title="Monthly Cohort Retention">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-gray-800">
            <th className="px-3 py-2 text-left text-gray-400 font-medium">Cohort</th>
            <th className="px-3 py-2 text-center text-gray-400 font-medium">Users</th>
            {periods.map(d => <th key={d} className="px-3 py-2 text-center text-gray-400 font-medium">Day {d}</th>)}
          </tr></thead>
          <tbody>
            {data.map(row => (
              <tr key={row.month} className="border-b border-gray-800/50">
                <td className="px-3 py-2 text-white font-medium">{row.month}</td>
                <td className="px-3 py-2 text-center text-gray-300">{row.total}</td>
                {periods.map(d => <td key={d} className={`px-3 py-2 text-center text-sm font-medium rounded ${cellColor(row.retention[d] ?? null)}`}>{row.retention[d] != null ? `${row.retention[d]}%` : '--'}</td>)}
              </tr>
            ))}
            {data.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-center text-gray-500">No cohort data yet</td></tr>}
          </tbody>
        </table>
      </Section>
      <div className="flex gap-3 text-[10px] text-gray-500">
        <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-emerald-900/40" /> 60%+</span>
        <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-teal-900/40" /> 40-59%</span>
        <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-yellow-900/40" /> 20-39%</span>
        <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-red-900/40" /> &lt;20%</span>
      </div>

      {/* Context note for low/zero retention */}
      <div className="bg-gray-900/50 border border-gray-800 rounded-lg px-4 py-3 text-xs">
        <div className="text-gray-400 flex items-start gap-2">
          <span className="text-blue-400 shrink-0">ℹ️</span>
          <div>
            <p className="text-gray-300 mb-1">Day 7 retention measures users who logged back in at least once after their signup day.</p>
            <p className="text-gray-500">Your beta cohort is new (Mar 2026). This metric will populate as users return over time.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TAB 8: UNIT ECONOMICS
   ═══════════════════════════════════════════════════════════════ */
function UnitEconomicsTab({ data }: { data: UnitEconData | null }) {
  if (!data) return <Loading />;
  const trendMax = Math.max(...data.monthlyTrend.map(m => m.total), 1);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Kpi value={`$${data.arpu}`} label="ARPU" sub="Revenue / Total Users" size="lg" />
        <Kpi value={`$${data.arppu}`} label="ARPPU" sub="Revenue / Paying Users" size="lg" />
        <Kpi value={`$${data.ltv}`} label="Est. LTV" sub={`Churn: ${data.churnRate}%`} size="lg" />
        <Kpi value={`$${data.totalRevenue.toLocaleString()}`} label="Total Revenue" sub={`Pack $${data.totalPackRevenue} + Sub $${data.totalSubRevenue}`} size="lg" />
      </div>

      <Section title="Monthly Revenue">
        {data.monthlyTrend.length === 0 || data.monthlyTrend.every(m => m.total === 0) ? (
          <div className="flex items-center justify-center text-gray-600 text-xs" style={{ minHeight: 200 }}>No revenue data yet</div>
        ) : (
          <div className="flex items-end gap-3" style={{ height: 200 }}>
            {data.monthlyTrend.map(m => (
              <div key={m.month} className="flex-1 flex flex-col items-center group relative" style={{ height: '100%', justifyContent: 'flex-end' }}>
                <div className="w-full flex flex-col justify-end" style={{ height: '100%' }}>
                  <div className="bg-purple-600 rounded-t" style={{ height: `${Math.max((m.sub / trendMax) * 100, m.sub > 0 ? 3 : 0)}%` }} />
                  <div className="bg-orange-500" style={{ height: `${Math.max((m.pack / trendMax) * 100, m.pack > 0 ? 3 : 0)}%` }} />
                </div>
                <div className="text-[9px] text-gray-500 mt-1">{m.month.slice(5)}</div>
                <div className="hidden group-hover:block absolute -top-10 bg-gray-800 text-[10px] text-white px-2 py-1 rounded z-10 whitespace-nowrap">{m.month}: ${m.total}</div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.topCustomers.length > 0 && (
          <Section title="Top Credit Users">
            {data.topCustomers.map((c, i) => (
              <div key={c._id} className="flex justify-between py-1.5 border-b border-gray-800/50 text-xs">
                <span><span className="text-gray-500">#{i + 1}</span> <span className="text-white ml-1">{c.name}</span></span>
                <span className="text-orange-400">{c.totalSpent} credits used</span>
              </div>
            ))}
          </Section>
        )}
        <Section title="Conversion Funnel">
          <div className="space-y-2">
            {[
              { label: 'Signups', count: data.funnel.signups },
              { label: 'Pipeline', count: data.funnel.startedPipeline },
              { label: 'Purchased', count: data.funnel.purchased },
              { label: 'Pro Sub', count: data.funnel.proSub },
            ].map(s => <Horiz key={s.label} label={s.label} count={s.count} pct={data.funnel.signups > 0 ? Math.round((s.count / data.funnel.signups) * 100) : 0} />)}
          </div>
        </Section>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TAB 9: ENGAGEMENT
   ═══════════════════════════════════════════════════════════════ */
function EngagementTab({ data }: { data: EngagementData | null }) {
  if (!data) return <Loading />;
  const maxPct = Math.max(...data.stepFunnel.map(s => s.pct), 1);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi value={`${data.onboardingRate}%`} label="Onboarding Rate" />
        <Kpi value={data.active7d} label="Active 7d" />
        <Kpi value={data.active30d} label="Active 30d" />
        <Kpi value={`${data.medianCompletionDays}d`} label="Median Completion" sub="Days to finish pipeline" />
      </div>

      <Section title={`Pipeline Step Funnel (${data.totalPipelines} total)`}>
        <div className="space-y-1.5">
          {data.stepFunnel.map((step, i) => {
            const conv = i > 0 && data.stepFunnel[i - 1].count > 0 ? Math.round((step.count / data.stepFunnel[i - 1].count) * 100) : 100;
            const convColor = conv >= 70 ? 'text-emerald-400' : conv >= 40 ? 'text-yellow-400' : 'text-red-400';
            return (
              <div key={step.label}>
                {i > 0 && <div className={`text-[10px] ${convColor} ml-36 mb-0.5`}>{conv}% conversion</div>}
                <Horiz label={step.label} count={step.count} pct={step.pct} />
              </div>
            );
          })}
        </div>
      </Section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title="Feature Adoption (Startups)">
          <div className="space-y-2">
            {Object.entries(data.intelAdoption).filter(([k]) => k !== 'totalStartups' && k !== 'total').map(([key, count]) => {
              const total = data.intelAdoption.totalStartups || 1;
              const pct = Math.round((count / total) * 100);
              return <Horiz key={key} label={key.replace(/([A-Z])/g, ' $1').trim()} count={count} pct={pct} color="bg-indigo-500" />;
            })}
          </div>
        </Section>

        <Section title="Credit Spending by Feature">
          <div className="space-y-1.5">
            {data.featureUsage.slice(0, 10).map(f => {
              const max = data.featureUsage[0]?.count || 1;
              return <Horiz key={f._id} label={f._id} count={f.totalCredits} pct={Math.round((f.count / max) * 100)} color="bg-orange-500" />;
            })}
            {data.featureUsage.length === 0 && <p className="text-gray-500 text-xs">No credit spending yet</p>}
          </div>
        </Section>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Kpi value={data.deepAnalysis.users} label="Deep Analysis Users" sub={`${data.deepAnalysis.rate}% of all users`} />
        <Kpi value={data.intelAdoption.totalStartups || 0} label="Total Startups Tracked" sub="With intel/deep/MVP features" />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TAB 10: FEEDBACK
   ═══════════════════════════════════════════════════════════════ */
function FeedbackTab({ data }: { data: FeedbackData | null }) {
  if (!data) return <Loading />;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi value={data.nps.score} label="NPS Score" sub={`P:${data.nps.promoters} Pa:${data.nps.passives} D:${data.nps.detractors}`} size="lg" />
        <Kpi value={data.nps.avgRating} label="Avg Rating" sub="Out of 5" />
        <Kpi value={data.totalFeedback} label="Total Feedback" />
        <Kpi value={data.betaUsers} label="Beta Users" sub={Object.entries(data.betaStatuses).map(([k, v]) => `${k}: ${v}`).join(', ')} />
        <Kpi value={data.nps.avgNps} label="Avg NPS" sub="Out of 10" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title="Feedback by Category">
          <div className="space-y-2">
            {Object.entries(data.categories).map(([cat, info]) => (
              <div key={cat} className="flex items-center justify-between py-1.5 border-b border-gray-800/50 text-xs">
                <span className="text-white font-medium capitalize">{cat}</span>
                <div className="flex items-center gap-3">
                  <span className="text-gray-400">{info.count} feedback</span>
                  <span className="text-yellow-400">{info.avgRating}/5</span>
                </div>
              </div>
            ))}
            {Object.keys(data.categories).length === 0 && <p className="text-gray-500 text-xs">No feedback yet</p>}
          </div>
        </Section>

        <Section title="Top Feature Requests">
          <div className="space-y-2">
            {data.featureRequests.map((fr, i) => (
              <div key={fr._id} className="py-1.5 border-b border-gray-800/50 text-xs">
                <div className="flex justify-between">
                  <span className="text-white font-medium">#{i + 1} {fr._id}</span>
                  <span className="text-brand-400">{fr.count} requests</span>
                </div>
                {fr.reasons.length > 0 && <div className="text-gray-500 mt-0.5 truncate">{fr.reasons[0]}</div>}
              </div>
            ))}
            {data.featureRequests.length === 0 && <p className="text-gray-500 text-xs">No feature requests yet</p>}
          </div>
        </Section>
      </div>

      {/* Feedback Collection Tools */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title="Send NPS Survey">
          <div className="space-y-3">
            <div>
              <label className="text-[10px] text-gray-500 block mb-1">Target audience</label>
              <select className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-brand-500">
                <option>All users</option>
                <option>Users who completed pipeline</option>
                <option>Pro subscribers</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] text-gray-500 block mb-1">Message</label>
              <textarea rows={3} defaultValue="On a scale of 0-10, how likely are you to recommend StartupScore to a friend or colleague?"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-brand-500 resize-none" />
            </div>
            <button className="px-4 py-1.5 text-xs bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition">Send Survey Email</button>
            <div className="text-[10px] text-gray-600">Last sent: Never</div>
          </div>
        </Section>

        <Section title="Beta Feedback Link">
          <div className="space-y-3">
            <p className="text-xs text-gray-400">Share this link with beta users to collect structured feedback:</p>
            <div className="flex gap-2">
              <input type="text" readOnly value="https://startupscore.live/feedback/beta"
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-gray-300 font-mono" />
              <button onClick={() => { navigator.clipboard.writeText('https://startupscore.live/feedback/beta'); }}
                className="px-3 py-1.5 text-xs bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition border border-gray-700">
                Copy
              </button>
            </div>
            <p className="text-[10px] text-gray-600">Responses will appear in this dashboard automatically.</p>
          </div>
        </Section>
      </div>

      {/* Recent feedback */}
      {data.recentFeedback.length > 0 && (
        <Section title="Recent Feedback">
          <div className="space-y-2">
            {data.recentFeedback.slice(0, 10).map(f => (
              <div key={f._id} className="py-2 border-b border-gray-800/50 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <Badge text={f.category} color="bg-gray-700 text-gray-300" />
                    <span className="text-gray-400 ml-2">{(f.betaUser as Record<string, string>)?.name || 'Unknown'}</span>
                    <span className="text-gray-600 ml-1">for {(f.startup as Record<string, string>)?.name || '?'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-yellow-400">{'★'.repeat(f.rating)}</span>
                    <span className="text-gray-500">NPS: {f.npsScore}</span>
                    <span className="text-gray-600">{new Date(f.submittedAt).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="text-gray-300 line-clamp-2">{f.comment}</div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TAB 11: ACTIVITY
   ═══════════════════════════════════════════════════════════════ */
function ActivityTab({ events }: { events: ActivityEvent[] }) {
  const cfg: Record<string, { color: string; icon: string }> = {
    signup: { color: 'text-blue-400 bg-blue-900/30', icon: '👤' },
    pipeline_complete: { color: 'text-green-400 bg-green-900/30', icon: '✅' },
    credit_purchase: { color: 'text-orange-400 bg-orange-900/30', icon: '💳' },
    credit_spend: { color: 'text-yellow-400 bg-yellow-900/30', icon: '⚡' },
    subscription: { color: 'text-purple-400 bg-purple-900/30', icon: '⭐' },
    pipeline_step: { color: 'text-blue-400 bg-blue-900/30', icon: '🚀' },
    intel_run: { color: 'text-indigo-400 bg-indigo-900/30', icon: '🧠' },
    upgrade: { color: 'text-emerald-400 bg-emerald-900/30', icon: '⬆️' },
    cancel: { color: 'text-red-400 bg-red-900/30', icon: '⬇️' },
    share: { color: 'text-cyan-400 bg-cyan-900/30', icon: '📊' },
    password_reset: { color: 'text-gray-400 bg-gray-800', icon: '🔑' },
  };
  if (events.length === 0) return <div className="text-gray-500 text-center py-8">No activity yet.</div>;
  return (
    <div className="bg-gray-900 rounded-xl border border-gray-800 divide-y divide-gray-800">
      {events.map((e, i) => {
        const c = cfg[e.type] || { color: 'text-gray-400 bg-gray-800', icon: '\u2022' };
        return (
          <div key={i} className="px-4 py-3 flex items-start gap-3">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs shrink-0 ${c.color}`}>{c.icon}</div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-white"><span className="font-medium">{e.user}</span> <span className="text-gray-500">({e.email})</span></div>
              <div className="text-xs text-gray-400 mt-0.5">{e.detail}</div>
            </div>
            <div className="text-[10px] text-gray-500 whitespace-nowrap shrink-0">{new Date(e.timestamp).toLocaleString()}</div>
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TAB 12: SETTINGS
   ═══════════════════════════════════════════════════════════════ */
const DEFAULT_CREDIT_COSTS: Record<string, number> = {
  PIPELINE_PROFILE: 1, PIPELINE_IDEA: 2, PIPELINE_REALITY: 2, PIPELINE_POSITION: 3, PIPELINE_VALIDATION: 3,
  INTEL_NAME: 2, INTEL_KEYWORDS: 3, INTEL_COMPETITORS: 3, INTEL_TRENDS: 2, INTEL_SENTIMENT: 3,
};

function SettingsTab() {
  const [signupCredits, setSignupCredits] = useState(10);
  const [banner, setBanner] = useState('');
  const [creditCosts, setCreditCosts] = useState<Record<string, number>>({ ...DEFAULT_CREDIT_COSTS });
  const [flags, setFlags] = useState<Record<string, boolean>>({
    intelligence: true, deepAnalysis: true, mvpBlueprint: true, financials: true, betaUsers: true,
  });
  const [saving, setSaving] = useState('');
  const [deleteStatus, setDeleteStatus] = useState('');

  const showSaved = (key: string) => { setSaving(key); setTimeout(() => setSaving(''), 2000); };

  const deleteTestData = async () => {
    if (!confirm('Delete all users with @test.com, @yopmail.com, @mailinator.com emails?')) return;
    setDeleteStatus('Deleting...');
    try {
      const { data } = await api.get('/admin/users', { params: { search: 'test.com', limit: 100 } });
      const testUsers = (data.data.users as AdminUser[]).filter(u =>
        u.email.endsWith('@test.com') || u.email.endsWith('@yopmail.com') || u.email.endsWith('@mailinator.com')
      );
      for (const u of testUsers) await api.delete(`/admin/users/${u._id}`);
      setDeleteStatus(`Deleted ${testUsers.length} test users`);
      setTimeout(() => setDeleteStatus(''), 3000);
    } catch { setDeleteStatus('Failed'); setTimeout(() => setDeleteStatus(''), 3000); }
  };

  const exportUsers = async () => {
    try {
      const { data } = await api.get('/admin/users', { params: { limit: 10000 } });
      const users = data.data.users as AdminUser[];
      const csv = [
        'Name,Email,Joined,Last Active,Credits,Spent,Pipelines,Pro',
        ...users.map(u => `"${u.name}","${u.email}",${new Date(u.createdAt).toLocaleDateString()},${u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : 'Never'},${u.credits},${u.totalSpent},${u.pipelineCount},${u.subscription?.status === 'active' ? 'Yes' : 'No'}`),
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `startupscore-users-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Export failed'); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Beta Controls */}
      <Section title="Beta Controls">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div><div className="text-xs text-white">Free credits on signup</div><div className="text-[10px] text-gray-500">Credits granted to new users on registration</div></div>
            <div className="flex items-center gap-2">
              <input type="number" min={0} max={100} value={signupCredits} onChange={e => setSignupCredits(parseInt(e.target.value) || 0)}
                className="w-16 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white text-center focus:outline-none focus:border-brand-500" />
              <button onClick={() => showSaved('signup')} className="px-3 py-1 text-[10px] bg-brand-600 text-white rounded hover:bg-brand-700 transition">
                {saving === 'signup' ? '✓ Saved' : 'Save'}
              </button>
            </div>
          </div>
          <div>
            <div className="text-xs text-white mb-1">Announcement banner</div>
            <div className="text-[10px] text-gray-500 mb-1">Leave empty to hide. Shows across the top of the app.</div>
            <div className="flex gap-2">
              <textarea value={banner} onChange={e => setBanner(e.target.value)} rows={2} placeholder="e.g. Welcome to our beta! Report bugs to support@startupscore.live"
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-xs text-white placeholder-gray-600 focus:outline-none focus:border-brand-500 resize-none" />
              <button onClick={() => showSaved('banner')} className="px-3 py-1 text-[10px] bg-brand-600 text-white rounded self-start hover:bg-brand-700 transition">
                {saving === 'banner' ? '✓ Saved' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </Section>

      {/* Credit Pricing */}
      <Section title="Credit Pricing">
        <table className="w-full text-xs">
          <thead><tr className="border-b border-gray-800">
            <th className="py-1.5 text-left text-gray-400 font-medium">Feature</th>
            <th className="py-1.5 text-center text-gray-400 font-medium w-24">Cost (credits)</th>
          </tr></thead>
          <tbody>
            {Object.entries(creditCosts).map(([key, cost]) => (
              <tr key={key} className="border-b border-gray-800/50">
                <td className="py-1.5 text-white font-mono text-[11px]">{key}</td>
                <td className="py-1.5 text-center">
                  <input type="number" min={0} max={50} value={cost} onChange={e => setCreditCosts(prev => ({ ...prev, [key]: parseInt(e.target.value) || 0 }))}
                    className="w-14 bg-gray-800 border border-gray-700 rounded px-2 py-0.5 text-xs text-white text-center focus:outline-none focus:border-brand-500" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button onClick={() => showSaved('costs')} className="mt-3 px-4 py-1.5 text-xs bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition">
          {saving === 'costs' ? '✓ Saved' : 'Save All Changes'}
        </button>
      </Section>

      {/* Feature Flags */}
      <Section title="Feature Flags">
        <div className="space-y-3">
          {Object.entries(flags).map(([key, enabled]) => (
            <div key={key} className="flex items-center justify-between">
              <span className="text-xs text-white capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
              <button onClick={() => setFlags(prev => ({ ...prev, [key]: !prev[key] }))}
                className={`w-10 h-5 rounded-full transition relative ${enabled ? 'bg-brand-600' : 'bg-gray-700'}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${enabled ? 'left-5' : 'left-0.5'}`} />
              </button>
            </div>
          ))}
        </div>
      </Section>

      {/* Danger Zone */}
      <Section title="Danger Zone">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-red-400">Reset all test data</div>
              <div className="text-[10px] text-gray-500">Deletes users with @test.com, @yopmail.com, @mailinator.com</div>
            </div>
            <button onClick={deleteTestData} className="px-3 py-1.5 text-[10px] bg-red-900/30 text-red-400 rounded-lg hover:bg-red-900/50 transition border border-red-900/50">
              {deleteStatus || 'Reset Test Data'}
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-white">Export all users CSV</div>
              <div className="text-[10px] text-gray-500">Download complete user list</div>
            </div>
            <button onClick={exportUsers} className="px-3 py-1.5 text-[10px] bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition">⬇ Export CSV</button>
          </div>
        </div>
      </Section>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SLIDE-OVER: USER DETAIL
   ═══════════════════════════════════════════════════════════════ */
function UserSlideOver({ data, onClose, onGrant, onTogglePro, onDelete }: {
  data: Record<string, unknown>;
  onClose: () => void;
  onGrant: (id: string, name: string, email: string) => void;
  onTogglePro: (id: string, name: string, email: string, isPro: boolean) => void;
  onDelete: (id: string, name: string, email: string) => void;
}) {
  const user = data.user as Record<string, unknown>;
  const pipelines = (data.pipelines as Array<Record<string, unknown>>) || [];
  const startups = (data.startups as Array<Record<string, unknown>>) || [];
  const creditHistory = ((user?.creditHistory as Array<Record<string, unknown>>) || []).slice(-20).reverse();
  const isPro = (user?.subscription as Record<string, string>)?.status === 'active';
  const userId = (user?._id as string) || '';
  const userName = (user?.name as string) || '';
  const userEmail = (user?.email as string) || '';
  const joinDate = user?.createdAt ? new Date(user.createdAt as string).toLocaleDateString() : 'Unknown';
  const lastActive = user?.lastLogin ? new Date(user.lastLogin as string).toLocaleDateString() : 'Never';
  const totalSpent = (user?.totalSpent as number) || 0;
  const totalPurchased = (user?.totalPurchased as number) || 0;
  const credits = (user?.credits as number) || 0;
  const completedPipelines = pipelines.filter(p => p.status === 'completed').length;

  return (
    <>
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-800">
        <div className="w-10 h-10 rounded-full bg-brand-600 flex items-center justify-center text-white font-bold text-sm">{userName.charAt(0).toUpperCase()}</div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-semibold text-sm">{userName}</div>
          <div className="text-gray-400 text-xs">{userEmail}</div>
        </div>
        <div className="flex items-center gap-2">
          {isPro && <Badge text="PRO" color="bg-purple-900/50 text-purple-400" />}
          <span className="text-[10px] text-gray-500">Joined {joinDate}</span>
        </div>
      </div>

      {/* Overview */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-gray-800/40 rounded-lg p-3">
          <div className="text-xs text-gray-400">Credits</div>
          <div className="text-white text-sm font-medium">{credits} available</div>
          <div className="text-[10px] text-gray-500">{totalPurchased} granted · {totalSpent} spent</div>
        </div>
        <div className="bg-gray-800/40 rounded-lg p-3">
          <div className="text-xs text-gray-400">Pipelines</div>
          <div className="text-white text-sm font-medium">{pipelines.length} started · {completedPipelines} completed</div>
          <div className="text-[10px] text-gray-500">Last active: {lastActive}</div>
        </div>
      </div>

      {/* Startups */}
      <h4 className="text-xs font-medium text-gray-400 mb-2">Startups ({startups.length})</h4>
      {startups.length === 0 ? <p className="text-gray-600 text-xs mb-3">No startups</p> : (
        <div className="space-y-1 mb-4">
          {startups.map(s => (
            <div key={s._id as string} className="flex items-center justify-between py-1.5 border-b border-gray-800/50 text-xs">
              <span className="text-white">{s.name as string}</span>
              <div className="flex items-center gap-2">
                <Badge text={s.stage as string} color="bg-gray-700 text-gray-400" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pipelines */}
      <h4 className="text-xs font-medium text-gray-400 mb-2">Pipelines ({pipelines.length})</h4>
      {pipelines.length === 0 ? <p className="text-gray-600 text-xs mb-3">No pipelines</p> : (
        <div className="space-y-1 mb-4">
          {pipelines.map(p => (
            <div key={p._id as string} className="flex items-center justify-between py-1.5 border-b border-gray-800/50 text-xs">
              <span className="text-white">{(p.title as string) || 'Untitled'}</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Step {Math.min(p.currentStep as number, 5)}/5</span>
                <Badge text={p.status as string} color={p.status === 'completed' ? 'bg-green-900/50 text-green-400' : p.status === 'active' ? 'bg-blue-900/50 text-blue-400' : 'bg-gray-700 text-gray-400'} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Credit History */}
      <h4 className="text-xs font-medium text-gray-400 mb-2">Credit History (last 20)</h4>
      {creditHistory.length === 0 ? <p className="text-gray-600 text-xs mb-3">No history</p> : (
        <div className="space-y-0.5 mb-4 max-h-48 overflow-y-auto">
          {creditHistory.map((ch, i) => (
            <div key={i} className="flex items-center justify-between py-1 border-b border-gray-800/50 text-[11px]">
              <div className="flex-1 min-w-0">
                <span className="text-gray-400">{ch.action as string}</span>
                {ch.pack && <span className="text-gray-500 ml-1">({ch.pack as string})</span>}
                {ch.note && <span className="text-gray-600 ml-1">— {ch.note as string}</span>}
              </div>
              <span className={`font-medium shrink-0 ml-2 ${(ch.amount as number) > 0 ? 'text-green-400' : 'text-red-400'}`}>
                {(ch.amount as number) > 0 ? '+' : ''}{ch.amount as number}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Admin Actions */}
      <div className="flex gap-2 pt-3 border-t border-gray-800">
        <button onClick={() => onGrant(userId, userName, userEmail)} className="flex-1 px-3 py-2 text-xs bg-emerald-900/30 text-emerald-400 rounded-lg hover:bg-emerald-900/50 transition">Grant Credits</button>
        <button onClick={() => onTogglePro(userId, userName, userEmail, isPro)} className={`flex-1 px-3 py-2 text-xs rounded-lg transition ${isPro ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50' : 'bg-purple-900/30 text-purple-400 hover:bg-purple-900/50'}`}>
          {isPro ? 'Remove Pro' : 'Upgrade Pro'}
        </button>
        <button onClick={() => onDelete(userId, userName, userEmail)} className="px-3 py-2 text-xs bg-red-900/20 text-red-500 rounded-lg hover:bg-red-900/40 transition">Delete</button>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SLIDE-OVER: STARTUP DETAIL
   ═══════════════════════════════════════════════════════════════ */
function StartupSlideOver({ data }: { data: Record<string, unknown> }) {
  const startup = data.startup as Record<string, unknown>;
  const scenarios = (data.scenarios as Array<Record<string, unknown>>) || [];
  const betaUsers = (data.betaUsers as Array<Record<string, unknown>>) || [];
  const feedbacks = (data.feedbacks as Array<Record<string, unknown>>) || [];
  const owner = startup?.owner as Record<string, string> | undefined;
  const intelligence = startup?.intelligence as Record<string, Record<string, unknown>> | undefined;
  const intelModules = intelligence ? Object.entries(intelligence).filter(([, v]) => v && typeof v === 'object' && 'analyzedAt' in v) : [];
  const positioning = startup?.positioning as Record<string, unknown> | undefined;
  const validationResults = startup?.validationResults as Record<string, unknown> | undefined;
  const pipeline = startup?.pipeline as Record<string, unknown> | undefined;
  const ideaText = (startup?.description as string) || (startup?.idea as string) || '';

  return (
    <>
      <div className="mb-4 pb-4 border-b border-gray-800">
        <div className="text-white font-semibold text-sm">{startup?.name as string || 'Unnamed'}</div>
        <div className="text-gray-400 text-xs mt-1">
          Owner: {owner?.name || '?'} ({owner?.email || '?'}) · Created {startup?.createdAt ? new Date(startup.createdAt as string).toLocaleDateString() : '?'}
        </div>
        <div className="flex gap-2 mt-2">
          <Badge text={startup?.stage as string || 'idea'} color="bg-blue-900/50 text-blue-400" />
          {(startup as Record<string, Record<string, unknown>>)?.deepAnalysis?.generatedAt && <Badge text="Deep Analysis" color="bg-orange-900/50 text-orange-400" />}
          {(startup as Record<string, Record<string, unknown>>)?.mvpBlueprint?.generatedAt && <Badge text="MVP Blueprint" color="bg-emerald-900/50 text-emerald-400" />}
        </div>
      </div>

      {/* Idea text */}
      {ideaText && (
        <div className="mb-4">
          <h4 className="text-xs font-medium text-gray-400 mb-1">Idea</h4>
          <p className="text-gray-300 text-xs leading-relaxed">{ideaText.slice(0, 500)}{ideaText.length > 500 ? '...' : ''}</p>
        </div>
      )}

      {/* Positioning */}
      {positioning && (
        <div className="mb-4">
          <h4 className="text-xs font-medium text-gray-400 mb-1">Positioning</h4>
          <div className="grid grid-cols-3 gap-2 text-[11px]">
            {positioning.niche && <div className="bg-gray-800/40 rounded p-2"><div className="text-gray-500">Niche</div><div className="text-white">{positioning.niche as string}</div></div>}
            {positioning.angle && <div className="bg-gray-800/40 rounded p-2"><div className="text-gray-500">Angle</div><div className="text-white">{positioning.angle as string}</div></div>}
            {positioning.revenueModel && <div className="bg-gray-800/40 rounded p-2"><div className="text-gray-500">Revenue</div><div className="text-white">{positioning.revenueModel as string}</div></div>}
          </div>
        </div>
      )}

      {/* Validation */}
      {validationResults && (
        <div className="mb-4">
          <h4 className="text-xs font-medium text-gray-400 mb-1">Validation Results</h4>
          <div className="bg-gray-800/40 rounded p-2 text-[11px] text-gray-300">
            {validationResults.submittedAt ? `Submitted ${new Date(validationResults.submittedAt as string).toLocaleDateString()}` : 'Not submitted yet'}
          </div>
        </div>
      )}

      {/* Intel modules */}
      {intelModules.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-medium text-gray-400 mb-1">Intelligence Modules ({intelModules.length})</h4>
          <div className="flex flex-wrap gap-1">
            {intelModules.map(([key]) => <Badge key={key} text={key} color="bg-blue-900/50 text-blue-400" />)}
          </div>
        </div>
      )}

      {/* Beta users & feedback */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gray-800/40 rounded-lg p-3">
          <div className="text-xs text-gray-400">Beta Users</div>
          <div className="text-white text-sm font-medium">{betaUsers.length}</div>
        </div>
        <div className="bg-gray-800/40 rounded-lg p-3">
          <div className="text-xs text-gray-400">Feedback</div>
          <div className="text-white text-sm font-medium">{feedbacks.length}</div>
        </div>
      </div>

      {scenarios.length > 0 && (
        <div className="mt-4">
          <h4 className="text-xs font-medium text-gray-400 mb-1">Financial Scenarios ({scenarios.length})</h4>
          {scenarios.slice(0, 3).map((s, i) => (
            <div key={i} className="text-[11px] text-gray-300 py-0.5 border-b border-gray-800/50">{s.name as string || `Scenario ${i + 1}`}</div>
          ))}
        </div>
      )}

      {/* View as user link */}
      <div className="mt-4 pt-3 border-t border-gray-800">
        <a href={`/startups/${startup?._id}/dashboard`} target="_blank" rel="noopener noreferrer"
          className="text-xs text-brand-400 hover:text-brand-300 transition">
          View as user →
        </a>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TAB: ANALYTICS — Built-in page view analytics
   ═══════════════════════════════════════════════════════════════ */
function AnalyticsTab({ data, period, setPeriod }: { data: AnalyticsData | null; period: string; setPeriod: (p: '24h' | '7d' | '30d' | '90d') => void }) {
  if (!data) return <Loading />;
  const s = data.summary;
  const timeSeries = data.viewsByHour || data.viewsByDay || [];
  const totalDevices = data.devices.desktop + data.devices.tablet + data.devices.mobile || 1;
  const maxViews = Math.max(...timeSeries.map(d => d.views), 1);

  const fmtDuration = (sec: number) => {
    if (sec < 60) return `${Math.round(sec)}s`;
    return `${Math.floor(sec / 60)}m ${Math.round(sec % 60)}s`;
  };

  const deviceData = [
    { label: 'Desktop', count: data.devices.desktop, pct: Math.round((data.devices.desktop / totalDevices) * 100), icon: '🖥', color: '#3b82f6' },
    { label: 'Mobile', count: data.devices.mobile, pct: Math.round((data.devices.mobile / totalDevices) * 100), icon: '📱', color: '#22c55e' },
    { label: 'Tablet', count: data.devices.tablet, pct: Math.round((data.devices.tablet / totalDevices) * 100), icon: '📟', color: '#a855f7' },
  ];

  return (
    <div className="space-y-6">
      {/* Header: period selector + live + GA badge */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="flex gap-1 bg-gray-900 rounded-lg p-1 border border-gray-800">
            {(['24h', '7d', '30d', '90d'] as const).map((p) => (
              <button key={p} onClick={() => setPeriod(p)}
                className={`px-3.5 py-1.5 text-xs rounded-md font-semibold transition-all ${period === p ? 'bg-brand-600 text-white shadow-lg shadow-brand-600/20' : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800'}`}>
                {p}
              </button>
            ))}
          </div>
          {/* GA active badge */}
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-950/40 border border-emerald-800/30">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[10px] font-semibold text-emerald-400">GA4 Active</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {data.liveUsers > 0 && (
            <div className="flex items-center gap-2 bg-emerald-950/30 border border-emerald-800/30 rounded-full px-3 py-1.5">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
              </span>
              <span className="text-emerald-400 text-xs font-bold">{data.liveUsers}</span>
              <span className="text-emerald-500/60 text-xs">online now</span>
            </div>
          )}
        </div>
      </div>

      {/* KPI Cards — redesigned */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="bg-gradient-to-br from-brand-600/10 to-gray-900 rounded-xl p-4 border border-brand-500/20 col-span-1">
          <div className="text-xs text-gray-500 font-medium mb-1">Total Views</div>
          <div className="text-2xl font-black text-white">{s.totalViews.toLocaleString()}</div>
          <div className="text-[10px] text-brand-400 mt-1">{period} period</div>
        </div>
        <div className="bg-gradient-to-br from-cyan-600/10 to-gray-900 rounded-xl p-4 border border-cyan-500/20">
          <div className="text-xs text-gray-500 font-medium mb-1">Unique Visitors</div>
          <div className="text-2xl font-black text-white">{s.uniqueVisitors.toLocaleString()}</div>
          <div className="text-[10px] text-cyan-400 mt-1">{s.uniqueUsers} authenticated</div>
        </div>
        <div className="bg-gradient-to-br from-amber-600/10 to-gray-900 rounded-xl p-4 border border-amber-500/20">
          <div className="text-xs text-gray-500 font-medium mb-1">Avg Duration</div>
          <div className="text-2xl font-black text-white">{fmtDuration(s.avgDuration)}</div>
          <div className="text-[10px] text-amber-400 mt-1">per session</div>
        </div>
        <div className="bg-gradient-to-br from-rose-600/10 to-gray-900 rounded-xl p-4 border border-rose-500/20">
          <div className="text-xs text-gray-500 font-medium mb-1">Bounce Rate</div>
          <div className="text-2xl font-black text-white">{s.bounceRate}%</div>
          <div className="text-[10px] text-rose-400 mt-1">{s.bounceRate < 50 ? 'healthy' : 'needs work'}</div>
        </div>
        <div className="bg-gradient-to-br from-emerald-600/10 to-gray-900 rounded-xl p-4 border border-emerald-500/20">
          <div className="text-xs text-gray-500 font-medium mb-1">Pages / Visitor</div>
          <div className="text-2xl font-black text-white">{s.uniqueVisitors > 0 ? (s.totalViews / s.uniqueVisitors).toFixed(1) : '0'}</div>
          <div className="text-[10px] text-emerald-400 mt-1">engagement depth</div>
        </div>
      </div>

      {/* Traffic chart — redesigned with custom bars */}
      <Section title={period === '24h' ? 'Traffic by Hour' : 'Traffic by Day'}>
        {timeSeries.length === 0 ? (
          <div className="text-gray-600 text-xs py-8 text-center">No traffic data for this period</div>
        ) : (
          <div>
            <div className="flex items-end gap-[2px] h-36 mt-2">
              {timeSeries.map((d, i) => {
                const pct = (d.views / maxViews) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center group relative" style={{ minWidth: 0 }}>
                    {/* Tooltip */}
                    <div className="absolute -top-10 bg-gray-800 border border-gray-700 rounded-lg px-2 py-1 text-[10px] text-white opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10 shadow-xl">
                      <span className="font-bold">{d.views}</span> views · <span className="text-gray-400">{d.visitors}</span> visitors
                    </div>
                    <div className="w-full rounded-t transition-all duration-300 group-hover:opacity-80" style={{
                      height: `${Math.max(pct, 2)}%`,
                      background: `linear-gradient(to top, rgba(124,111,247,0.6), rgba(124,111,247,0.25))`,
                    }} />
                  </div>
                );
              })}
            </div>
            {/* X-axis labels — show every few */}
            <div className="flex gap-[2px] mt-1">
              {timeSeries.map((d, i) => {
                const showLabel = timeSeries.length <= 14 || i % Math.ceil(timeSeries.length / 10) === 0;
                return (
                  <div key={i} className="flex-1 text-center" style={{ minWidth: 0 }}>
                    {showLabel && <span className="text-[9px] text-gray-600">{period === '24h' ? d.date.slice(11, 16) : d.date.slice(5)}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Top Pages — wider */}
        <div className="lg:col-span-2">
          <Section title="Top Pages">
            {data.topPages.length === 0 ? (
              <div className="text-gray-600 text-xs py-6 text-center">No page views yet</div>
            ) : (
              <div className="space-y-1">
                {data.topPages.slice(0, 12).map((p, i) => {
                  const maxPv = data.topPages[0]?.views || 1;
                  const barW = Math.max((p.views / maxPv) * 100, 4);
                  return (
                    <div key={p.path} className="group flex items-center gap-3 py-1.5 hover:bg-gray-800/30 rounded-lg px-2 transition">
                      <span className="text-[10px] text-gray-600 w-4 text-right shrink-0">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-300 truncate font-medium" title={p.path}>{p.path}</div>
                        <div className="h-1 rounded-full bg-gray-800 mt-1 overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-brand-600 to-brand-400 transition-all duration-500" style={{ width: `${barW}%` }} />
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs font-bold text-white">{p.views}</div>
                        <div className="text-[10px] text-gray-500">{p.uniqueVisitors} uniq</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {/* Device breakdown — donut style */}
          <Section title="Devices">
            <div className="flex items-center gap-4">
              {/* Mini donut */}
              <div className="relative w-20 h-20 shrink-0">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                  {(() => {
                    let offset = 0;
                    return deviceData.map((d) => {
                      const dash = d.pct;
                      const el = (
                        <circle key={d.label} cx="18" cy="18" r="14" fill="none" strokeWidth="4"
                          stroke={d.color} strokeDasharray={`${dash} ${100 - dash}`} strokeDashoffset={-offset}
                          className="transition-all duration-700" opacity={d.count > 0 ? 1 : 0.15} />
                      );
                      offset += dash;
                      return el;
                    });
                  })()}
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-[10px] font-bold text-gray-400">{(data.devices.desktop + data.devices.tablet + data.devices.mobile).toLocaleString()}</span>
                </div>
              </div>
              <div className="space-y-2 flex-1">
                {deviceData.map(d => (
                  <div key={d.label} className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: d.color }} />
                    <span className="text-xs text-gray-400 flex-1">{d.icon} {d.label}</span>
                    <span className="text-xs font-bold text-white">{d.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          </Section>

          {/* Top Referrers */}
          <Section title="Top Referrers">
            {data.referrers.length === 0 ? (
              <div className="text-gray-600 text-xs py-4 text-center">No referrer data</div>
            ) : (
              <div className="space-y-1.5">
                {data.referrers.slice(0, 8).map((r, i) => (
                  <div key={r.source} className="flex items-center gap-2 text-xs py-1 hover:bg-gray-800/30 rounded px-1 transition">
                    <span className="text-gray-600 w-3 text-right text-[10px]">{i + 1}</span>
                    <span className="text-gray-300 truncate flex-1" title={r.source}>{r.source.replace(/^https?:\/\//, '').replace(/\/$/, '')}</span>
                    <span className="text-gray-500 font-medium shrink-0 bg-gray-800 px-1.5 py-0.5 rounded">{r.count}</span>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* GA4 link */}
          <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-4">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-4 h-4 text-amber-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.84 2.998V21.003c0 .55-.449.997-1 .997H14.56c-.55 0-1-.447-1-.997V2.998c0-.55.449-.998 1-.998h7.28c.55 0 1 .448 1 .998zM13.21 16.396a3.598 3.598 0 01-3.604 3.604 3.598 3.598 0 01-3.604-3.604 3.598 3.598 0 013.604-3.604 3.598 3.598 0 013.604 3.604zM9.466 2c.55 0 1 .448 1 .998v7.123l-5.467 5.466A1.002 1.002 0 014 15.235V2.998c0-.55.449-.998 1-.998h4.466z"/>
              </svg>
              <span className="text-xs font-bold text-white">Google Analytics 4</span>
            </div>
            <p className="text-[11px] text-gray-500 mb-2">Full GA4 dashboard with real-time data, conversion funnels, and audience insights.</p>
            <a href="https://analytics.google.com/analytics/web/#/p/G-BP081Z5JYZ" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-400 hover:text-amber-300 transition">
              Open GA4 Dashboard
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SHARED: PAGINATION
   ═══════════════════════════════════════════════════════════════ */
function Pagination({ page, pages, setPage }: { page: number; pages: number; setPage: (p: number) => void }) {
  return (
    <div className="flex items-center gap-2 justify-center">
      <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
        className="px-3 py-1 text-xs rounded bg-gray-800 text-gray-300 hover:bg-gray-700 disabled:opacity-50 transition">Prev</button>
      <span className="text-xs text-gray-400">Page {page} of {pages}</span>
      <button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages}
        className="px-3 py-1 text-xs rounded bg-gray-800 text-gray-300 hover:bg-gray-700 disabled:opacity-50 transition">Next</button>
    </div>
  );
}
